/**
 * WorkflowPage — the ONE Generic Workflow Page.
 *
 * Embedded mode (inside WorkflowSectionPage) shows 3 agent tabs:
 *  - My Queue:  assigned to this employee (capacity-limited)
 *  - Backlog:   this employee's no_answer + postponed entries
 *  - Completed: resolved/archived entries with search filters + Re-open
 *
 * Manager mode (standalone) shows phase tabs + widgets from the Registry.
 */

import { useCallback, useEffect, useState } from 'react'
import { Badge, Banner, BlockStack, Button, Card, FormLayout, Modal, Page, Select, Spinner, Text, TextField } from '@shopify/polaris'
import { RefreshIcon } from '@shopify/polaris-icons'
import { WorkflowQueueTable } from './WorkflowQueueTable'
import { useWorkflowPage }    from './useWorkflowPage'
import { useAppSession }      from '../../hooks/useAppSession'
import { engineApi }          from '../../sdk/engine.api'
import type { WorkflowId, EnginePhase, UiTabSpec, QueueEntry } from '../../sdk/engine.api'
import { getDistributionConfig } from '../../sdk/distribution.api'
import type { QueueWorkflowId } from '../../sdk/distribution.api'

const QUEUE_WORKFLOW_IDS = new Set<string>(['confirmation', 'sales', 'shipping_confirmation', 'customer_service'])

type Props = {
  workflowId:      WorkflowId
  backUrl?:        string
  embedded?:       boolean
  agentFilter?:    boolean
  /** Increment to trigger a My Queue refresh from outside the component */
  refreshSignal?:  number
}

type AgentTabId = 'my-queue' | 'backlog' | 'completed'
const AGENT_TABS: { id: AgentTabId; label: string }[] = [
  { id: 'my-queue',   label: 'My Queue'  },
  { id: 'backlog',    label: 'Backlog'   },
  { id: 'completed',  label: 'Completed' },
]

type CompletedSearch = { query: string }

// ── Backlog tab (own no_answer + postponed orders) ────────────────────────────

function BacklogTab({
  shop, workflowId, employeeId, surface, permissions,
  onMutated,
}: {
  shop:        string
  workflowId:  WorkflowId
  employeeId:  string
  surface:     ReturnType<typeof useWorkflowPage>['state']['surface']
  permissions: ReturnType<typeof useWorkflowPage>['state']['permissions']
  onMutated:   () => void
}) {
  const [items,   setItems]   = useState<QueueEntry[]>([])
  const [loading, setLoading] = useState(false)
  // True while we wait for distribution config — prevents a premature load with
  // the default mode that would be immediately superseded by the real config.
  const [configLoading, setConfigLoading] = useState(() => QUEUE_WORKFLOW_IDS.has(workflowId))
  const [backlogMode, setBacklogMode] = useState<'stay_assigned' | 'return_to_pool'>('stay_assigned')

  // G-13: load backlogAssignmentMode once per mount for queue-type workflows
  useEffect(() => {
    if (!QUEUE_WORKFLOW_IDS.has(workflowId)) return
    getDistributionConfig(workflowId as QueueWorkflowId)
      .then(cfg => { if (cfg.backlogAssignmentMode) setBacklogMode(cfg.backlogAssignmentMode) })
      .catch(() => { /* keep default */ })
      .finally(() => setConfigLoading(false))
  }, [workflowId])

  const load = useCallback(async () => {
    if (!employeeId || configLoading) return
    setLoading(true)
    try {
      if (backlogMode === 'return_to_pool') {
        // Pool mode: backlog orders are unassigned (phase=queued), show all regardless of agent
        const [r1, r2] = await Promise.all([
          engineApi.queue(shop, workflowId, { phase: ['queued'] as EnginePhase[], status: 'no_answer',  limit: 100 }),
          engineApi.queue(shop, workflowId, { phase: ['queued'] as EnginePhase[], status: 'postponed', limit: 100 }),
        ])
        setItems([...r1.items, ...r2.items].sort((a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        ))
      } else {
        // Default: only this agent's assigned backlog items
        const [r1, r2] = await Promise.all([
          engineApi.queue(shop, workflowId, { phase: ['assigned'] as EnginePhase[], agent: employeeId, status: 'no_answer',  limit: 100 }),
          engineApi.queue(shop, workflowId, { phase: ['assigned'] as EnginePhase[], agent: employeeId, status: 'postponed', limit: 100 }),
        ])
        setItems([...r1.items, ...r2.items].sort((a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        ))
      }
    } finally {
      setLoading(false)
    }
  }, [shop, workflowId, employeeId, backlogMode, configLoading])

  useEffect(() => { load() }, [load])

  if (!surface || !permissions) return null

  return (
    <WorkflowQueueTable
      items={items}
      total={items.length}
      limit={items.length || 1}
      offset={0}
      surface={surface}
      permissions={permissions}
      shop={shop}
      loading={loading}
      onNextPage={() => {}}
      onPrevPage={() => {}}
      onMutated={() => { load(); onMutated() }}
    />
  )
}

// ── Completed tab (resolved/archived with search + Re-open) ───────────────────

function CompletedTab({
  shop, workflowId, surface, permissions,
  onMutated,
}: {
  shop:        string
  workflowId:  WorkflowId
  surface:     ReturnType<typeof useWorkflowPage>['state']['surface']
  permissions: ReturnType<typeof useWorkflowPage>['state']['permissions']
  onMutated:   () => void
}) {
  const [items,   setItems]   = useState<QueueEntry[]>([])
  const [total,   setTotal]   = useState(0)
  const [offset,  setOffset]  = useState(0)
  const [loading, setLoading] = useState(false)
  const [search,  setSearch]  = useState<CompletedSearch>({ query: '' })
  const LIMIT = 50

  const load = useCallback(async (off: number) => {
    setLoading(true)
    try {
      const res = await engineApi.queue(shop, workflowId, {
        phase: ['resolved', 'archived'] as EnginePhase[],
        limit: LIMIT,
        offset: off,
      })
      setItems(res.items)
      setTotal(res.total)
      setOffset(off)
    } finally {
      setLoading(false)
    }
  }, [shop, workflowId])

  useEffect(() => { load(0) }, [load])

  // Client-side filter — single query matched against all identifiers
  const filtered = items.filter(e => {
    const q = search.query.trim().toLowerCase()
    if (!q) return true
    return [
      e.orderRef.orderName,
      String(e.meta['customer_name']  ?? ''),
      String(e.meta['customer_phone'] ?? ''),
      String(e.meta['phone']          ?? ''),
      String(e.meta['email']          ?? ''),
    ].some(field => field.toLowerCase().includes(q))
  })

  const handleReopen = async (entry: QueueEntry) => {
    await engineApi.reopen(shop, workflowId, entry.id)
    load(offset)
    onMutated()
  }

  if (!surface || !permissions) return null

  return (
    <BlockStack gap="300">
      {/* Single search bar */}
      <TextField
        label=""
        labelHidden
        placeholder="Search by name, phone, order #, email…"
        value={search.query}
        onChange={v => setSearch({ query: v })}
        clearButton
        onClearButtonClick={() => setSearch({ query: '' })}
        autoComplete="off"
      />

      {/* Table — with Re-open button per row via custom render */}
      <CompletedTable
        items={filtered}
        total={total}
        limit={LIMIT}
        offset={offset}
        surface={surface}
        permissions={permissions}
        shop={shop}
        loading={loading}
        onNextPage={() => load(offset + LIMIT)}
        onPrevPage={() => load(Math.max(0, offset - LIMIT))}
        onMutated={() => { load(offset); onMutated() }}
        onReopen={handleReopen}
      />
    </BlockStack>
  )
}

// Completed table wraps WorkflowQueueTable and injects a Re-open button into
// the expanded panel via onMutated. The Re-open is exposed as a top-level
// button in the panel (not an outcome).
function CompletedTable({
  items, total, limit, offset, surface, permissions,
  shop, loading, onNextPage, onPrevPage, onMutated, onReopen,
}: {
  items:       QueueEntry[]
  total:       number
  limit:       number
  offset:      number
  surface:     NonNullable<ReturnType<typeof useWorkflowPage>['state']['surface']>
  permissions: NonNullable<ReturnType<typeof useWorkflowPage>['state']['permissions']>
  shop:        string
  loading:     boolean
  onNextPage:  () => void
  onPrevPage:  () => void
  onMutated:   () => void
  onReopen:    (entry: QueueEntry) => void
}) {
  const [reopening, setReopening] = useState<string | null>(null)

  const handleReopen = async (entry: QueueEntry) => {
    setReopening(entry.id)
    try { await onReopen(entry) } finally { setReopening(null) }
  }

  return (
    <WorkflowQueueTable
      items={items}
      total={total}
      limit={limit}
      offset={offset}
      surface={surface}
      permissions={permissions}
      shop={shop}
      loading={loading}
      onNextPage={onNextPage}
      onPrevPage={onPrevPage}
      onMutated={onMutated}
      extraPanelAction={(entry) => (
        <Button
          size="slim"
          loading={reopening === entry.id}
          onClick={() => handleReopen(entry)}
          disabled={entry.phase !== 'resolved' && entry.phase !== 'archived'}
        >
          Re-open
        </Button>
      )}
    />
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function WorkflowPage({ workflowId, backUrl = '/workspace-hub', embedded = false, agentFilter = false, refreshSignal }: Props) {
  const { session }        = useAppSession()
  const employeeId         = session?.employeeId ?? ''
  const { state, actions } = useWorkflowPage(workflowId, agentFilter ? employeeId : undefined)
  const { surface, queue, filters, loading, queueLoading, error } = state
  const { can, setFilter, nextPage, prevPage, refreshQueue } = actions

  const [activeTabId,    setActiveTabId]    = useState<string | null>(null)
  const [activeAgentTab, setActiveAgentTab] = useState<AgentTabId>('my-queue')

  // G-21: Add-to-queue modal — must be declared before any early return
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [addForm,      setAddForm]      = useState({ shopifyOrderId: '', orderName: '', priority: 'normal' as 'normal' | 'high' | 'critical' })
  const [addLoading,   setAddLoading]   = useState(false)
  const [addError,     setAddError]     = useState<string | null>(null)
  const [addSuccess,   setAddSuccess]   = useState<string | null>(null)

  // Set My Queue filter on mount (embedded mode)
  useEffect(() => {
    if (embedded && employeeId) {
      setFilter({ phase: ['assigned', 'in_session'] as EnginePhase[], agent: employeeId, status: undefined, meta: undefined })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, employeeId])

  // Refresh My Queue when parent signals (e.g., after Pull More)
  useEffect(() => {
    if (embedded && refreshSignal !== undefined && refreshSignal > 0) {
      refreshQueue()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal])

  function handleAgentTabChange(tabId: AgentTabId) {
    setActiveAgentTab(tabId)
    if (tabId === 'my-queue') {
      setFilter({ phase: ['assigned', 'in_session'] as EnginePhase[], agent: employeeId, status: undefined, meta: undefined })
    }
    // backlog + completed have their own loaders — no shared queue filter needed
  }

  if (loading && !surface) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner />
      </div>
    )
  }

  if (error && !surface) {
    if (embedded) return <Banner tone="critical"><Text as="p">{error}</Text></Banner>
    return (
      <Page backAction={{ content: 'Back', url: backUrl }} title="Workflow">
        <Banner tone="critical"><Text as="p">{error}</Text></Banner>
      </Page>
    )
  }

  const { definition, ui, statusSet, runtime } = surface!
  const perms = state.permissions!

  const healthTone =
    runtime.health === 'ok'       ? 'success' :
    runtime.health === 'degraded' ? 'warning' : 'critical'

  type AllTab = { id: null; label: string; phase?: undefined; status?: undefined; meta?: undefined }
  type AnyTab = AllTab | UiTabSpec
  const tabs: AnyTab[] = ui.tabs?.length
    ? [{ id: null, label: 'All' } as AllTab, ...ui.tabs]
    : []

  function handleTabClick(tabId: string | null, tab: AnyTab | undefined) {
    setActiveTabId(tabId)
    if (!tab || tab.id === null) { setFilter({ phase: undefined, status: undefined, meta: undefined }); return }
    setFilter({
      phase:  tab.phase as EnginePhase[] | undefined,
      status: tab.status,
      meta:   tab.meta,
    })
  }

  const statusOptions = [
    { label: 'All statuses', value: '' },
    ...statusSet.statuses.filter(s => s.visible).map(s => ({ label: s.name, value: s.id })),
  ]

  const widgets = ui.widgets ?? []

  const shop = state.shop

  async function handleAddToQueue() {
    if (!addForm.shopifyOrderId.trim() || !addForm.orderName.trim()) {
      setAddError('Order ID and Order Name are required')
      return
    }
    setAddLoading(true); setAddError(null); setAddSuccess(null)
    try {
      const res = await engineApi.addToQueue(shop, workflowId, {
        shopifyOrderId: addForm.shopifyOrderId.trim(),
        orderName:      addForm.orderName.trim(),
        priority:       addForm.priority,
      })
      setAddSuccess(res.created ? `Order ${addForm.orderName} added to queue` : `Order ${addForm.orderName} already in queue`)
      setAddForm({ shopifyOrderId: '', orderName: '', priority: 'normal' })
      refreshQueue()
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to add order')
    } finally {
      setAddLoading(false)
    }
  }

  // Embedded: content area is determined by active agent tab
  if (embedded) {
    return (
      <div style={{ paddingTop: 16 }}>
        <BlockStack gap="400">
          {error && <Banner tone="critical"><Text as="p">{error}</Text></Banner>}
          {runtime.paused && (
            <Banner tone="warning">
              <Text as="p">This workflow is paused. Orders are not being processed.</Text>
            </Banner>
          )}

          {/* Agent tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e1e3e5' }}>
            {AGENT_TABS.map(tab => {
              const active = activeAgentTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => handleAgentTabChange(tab.id)}
                  style={{
                    padding: '10px 20px', fontSize: 14, border: 'none',
                    background: 'transparent',
                    borderBottom: active ? '2px solid #2c6ecb' : '2px solid transparent',
                    color: active ? '#2c6ecb' : '#6d7175',
                    cursor: 'pointer', marginBottom: -2,
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* My Queue */}
          {activeAgentTab === 'my-queue' && (
            <BlockStack gap="300">
              <Button icon={RefreshIcon} variant="plain" onClick={refreshQueue} loading={queueLoading}>
                Refresh
              </Button>
              {queue && (
                <WorkflowQueueTable
                  items={queue.items}
                  total={queue.total}
                  limit={queue.limit}
                  offset={queue.offset}
                  surface={surface!}
                  permissions={perms}
                  shop={shop}
                  loading={queueLoading}
                  onNextPage={nextPage}
                  onPrevPage={prevPage}
                  onMutated={refreshQueue}
                />
              )}
            </BlockStack>
          )}

          {/* Backlog */}
          {activeAgentTab === 'backlog' && (
            <BacklogTab
              shop={shop}
              workflowId={workflowId}
              employeeId={employeeId}
              surface={surface}
              permissions={perms}
              onMutated={refreshQueue}
            />
          )}

          {/* Completed */}
          {activeAgentTab === 'completed' && (
            <CompletedTab
              shop={shop}
              workflowId={workflowId}
              surface={surface}
              permissions={perms}
              onMutated={refreshQueue}
            />
          )}
        </BlockStack>
      </div>
    )
  }

  // Manager (standalone) mode
  const queueContent = (
    <BlockStack gap="400">
      {error && <Banner tone="critical"><Text as="p">{error}</Text></Banner>}
      {runtime.paused && (
        <Banner tone="warning">
          <Text as="p">This workflow is paused. Orders are not being processed.</Text>
        </Banner>
      )}

      {widgets.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {widgets.map(w => (
            <Card key={w.id}>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">{w.label}</Text>
                <Text as="h2" variant="headingLg" tone="base">—</Text>
              </BlockStack>
            </Card>
          ))}
        </div>
      )}

      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e1e3e5' }}>
          {tabs.map(tab => (
            <button
              key={String(tab.id)}
              onClick={() => handleTabClick(tab.id, tab)}
              style={{
                padding: '8px 16px', fontSize: 14, border: 'none',
                background: activeTabId === tab.id ? '#fff' : 'transparent',
                borderBottom: activeTabId === tab.id ? '2px solid #2c6ecb' : '2px solid transparent',
                color: activeTabId === tab.id ? '#2c6ecb' : '#6d7175',
                cursor: 'pointer', marginBottom: -2, fontWeight: activeTabId === tab.id ? 600 : 400,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {ui.filters.map(f => {
          if (f.key === 'status') {
            return (
              <div key={f.key} style={{ minWidth: 160 }}>
                <Select
                  label={f.label}
                  options={statusOptions}
                  value={filters.status ?? ''}
                  onChange={v => setFilter({ status: v || undefined })}
                />
              </div>
            )
          }
          return null
        })}
        {(filters.status || filters.phase || filters.meta) && (
          <Button variant="plain" onClick={() => setFilter({ status: undefined, phase: undefined, meta: undefined })}>
            Clear filters
          </Button>
        )}
        {can('queue.assign') && (
          <div style={{ marginLeft: 'auto' }}>
            <Button size="slim" onClick={() => { setAddModalOpen(true); setAddError(null); setAddSuccess(null) }}>
              Add to Queue
            </Button>
          </div>
        )}
      </div>

      {/* G-21: Add to Queue modal */}
      <Modal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add Order to Queue"
        primaryAction={{ content: 'Add', onAction: handleAddToQueue, loading: addLoading }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setAddModalOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            {addError   && <Banner tone="critical"><p>{addError}</p></Banner>}
            {addSuccess && <Banner tone="success"><p>{addSuccess}</p></Banner>}
            <TextField
              label="Shopify Order ID"
              value={addForm.shopifyOrderId}
              onChange={v => setAddForm(f => ({ ...f, shopifyOrderId: v }))}
              placeholder="gid://shopify/Order/12345"
              autoComplete="off"
              helpText="The Shopify GID for this order"
            />
            <TextField
              label="Order Name"
              value={addForm.orderName}
              onChange={v => setAddForm(f => ({ ...f, orderName: v }))}
              placeholder="#1001"
              autoComplete="off"
            />
            <Select
              label="Priority"
              options={[
                { label: 'Normal', value: 'normal' },
                { label: 'High',   value: 'high'   },
                { label: 'Critical', value: 'critical' },
              ]}
              value={addForm.priority}
              onChange={v => setAddForm(f => ({ ...f, priority: v as typeof addForm.priority }))}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {can('queue.assign') && (ui.bulkActions?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', gap: 8 }}>
          {(ui.bulkActions ?? []).filter(a => can(a.permission)).map(a => (
            <Button key={a.id} size="slim">{a.label}</Button>
          ))}
        </div>
      )}

      {queue && (
        <WorkflowQueueTable
          items={queue.items}
          total={queue.total}
          limit={queue.limit}
          offset={queue.offset}
          surface={surface!}
          permissions={perms}
          shop={shop}
          loading={queueLoading}
          onNextPage={nextPage}
          onPrevPage={prevPage}
          onMutated={refreshQueue}
        />
      )}
    </BlockStack>
  )

  return (
    <Page
      title={definition.name}
      titleMetadata={
        runtime.paused
          ? <Badge tone="warning">Paused</Badge>
          : <Badge tone={healthTone}>{runtime.health}</Badge>
      }
      backAction={{ content: 'Back', url: backUrl }}
      primaryAction={{
        content: 'Refresh',
        icon: RefreshIcon,
        onAction: refreshQueue,
        loading: queueLoading,
      }}
      secondaryActions={[
        ...(can('workflow.pause') && !runtime.paused ? [{ content: 'Pause', onAction: () => {} }] : []),
        ...(can('workflow.pause') &&  runtime.paused ? [{ content: 'Resume', onAction: () => {} }] : []),
        ...(can('queue.distribute') ? [{ content: 'Distribute Queue', onAction: () => {} }] : []),
      ]}
    >
      {queueContent}
    </Page>
  )
}
